import { create } from 'zustand';

import {
  IAdvisorDashboard,
  IAdvisorInfo,
  ICurrentSubscriptionResponse,
  IGetOrganisationProfileResponse,
  IGetSubscriptionPlansResponse,
  IInvoiceDetailResponse,
  IProfileSetupProgressResponse,
  IUserInfo,
} from '@zeta-types';
import {
  IOrgDetailsStore,
  IProfileProgressStore,
  ISubscriptionPlansStore,
  ISubscriptionStore,
  IUseDefaultEnumsStore,
  IUseDirtyFieldsStore,
  IUseInvestorIDStore,
  IUserAccess,
  IUserAccessInput,
  IUserAccessStore,
  IUserPreferenceStore,
  IUserPreferences,
  IUserStore,
} from './types';

type UserAccessConfigInput = {
  Type?: 'Read' | 'Write';
  type?: 'Read' | 'Write';
  StatusCondition?: string;
  status_condition?: string;
  DisabledSubFeatures?: string[];
  disabled_sub_features?: string[];
};

const normalizeUserAccess = (userAccess: IUserAccess | IUserAccessInput): IUserAccess => {
  const normalizedAccess = Object.entries(userAccess?.access || {}).reduce<IUserAccess['access']>((acc, [featureName, configs]) => {
    acc[featureName] = (configs || []).map((config: UserAccessConfigInput) => ({
      Type: config.Type ?? config.type ?? 'Read',
      StatusCondition: config.StatusCondition ?? config.status_condition ?? '',
      DisabledSubFeatures: config.DisabledSubFeatures ?? config.disabled_sub_features ?? [],
    }));

    return acc;
  }, {});

  return { access: normalizedAccess };
};

export const useUserDetailsStore = create<IUserStore>((set) => ({
  user: {} as IUserInfo,
  planningStatus: '',
  updateUser: (x: IUserInfo) => set(() => ({ user: x })),
  updatePlanningStatus: (x: string) => set(() => ({ planningStatus: x })),
}));

export const useAdvisorDetailsStore = create((set) => ({
  advisor: {} as IAdvisorInfo,
  dashboardData: {} as IAdvisorDashboard,
  updateAdvisor: (x: IAdvisorInfo) => set(() => ({ advisor: x })),
  updateDashboard: (x: IAdvisorDashboard) => set(() => ({ dashboardData: x })),
}));

export const useSubscriptionDetailsStore = create<ISubscriptionStore>((set) => ({
  subscription: null as ICurrentSubscriptionResponse | null,
  loading: true,
  updateSubscription: (x: ICurrentSubscriptionResponse | null) => set(() => ({ subscription: x })),
  updateLoading: (x: boolean) => set(() => ({ loading: x })),
}));

export const useSubscriptionPlansStore = create<ISubscriptionPlansStore>((set) => ({
  subscriptionPlans: [] as IGetSubscriptionPlansResponse[],
  updateSubscriptionPlans: (x: IGetSubscriptionPlansResponse[]) => set(() => ({ subscriptionPlans: x })),
  loading: true,
  updateLoading: (x: boolean) => set(() => ({ loading: x })),
}));

export interface IInvoiceDetailStore {
  invoices: IInvoiceDetailResponse['invoices'];
  suspended: boolean;
  updateInvoiceDetail: (x: IInvoiceDetailResponse) => void;
}

export const useInvoiceDetailStore = create<IInvoiceDetailStore>((set) => ({
  invoices: [],
  suspended: false,
  updateInvoiceDetail: (x: IInvoiceDetailResponse) =>
    set(() => ({
      invoices: x.invoices || [],
      suspended: x.suspended || false,
    })),
}));

export const useDirtyFieldsStore = create<IUseDirtyFieldsStore>((set) => ({
  dirtyFieldsState: false,
  updateDirtyFieldsState: (x: boolean) => set(() => ({ dirtyFieldsState: x })),
}));

export const useInvestorIDStore = create<IUseInvestorIDStore>((set) => ({
  investorID: '',
  updateInvestorID: (x: string) => set(() => ({ investorID: x })),
}));

export const useUserPreferenceStore = create<IUserPreferenceStore>((set) => ({
  preference: {
    organisationPreference: {
      tableView: 0,
    },
    policyPreference: {
      tableView: 0,
    },
    tenantPreference: {
      tableView: 1,
    },
    casePreference: {
      tableDense: false,
    },
    transactionPreference: {
      tableView: 1,
    },
    clientPreference: {
      tableDense: false,
    },
    globalPreference: {
      mode: 'light',
      stretch: true,
      tableDense: false,
    },
    notificationPreference: {
      tableDense: false,
    },
  } as IUserPreferences,
  updateUserPreference: (x: IUserPreferences) => set(() => ({ preference: x })),
}));

export const useProfileProgressStore = create<IProfileProgressStore>((set) => ({
  progressData: {
    progressPercentage: 10,
    firmProfileProgress: {
      uploadCompanyLogo: false,
      manageFirmRoles: null,
      modelPortfolios: false,
      riskAssessment: false,
      financialPlanning: false,
      adviceTemplates: false,
      productConfig: false,
      factFindFieldConfig: false,
      billingInvoice: false,
    },
    myUserProfileProgress: {
      updateProfilePicture: false,
      addCertificateNExpertise: false,
      addWorkingHours: false,
      setNotificationPref: false,
    },
    firmProfileSetupComplete: false,
  },
  status: false,
  updateProfileSetupStatus: (x: IProfileSetupProgressResponse | null, status: boolean) => set(() => ({ progressData: x, status: status })),
}));

export const useOrgDetailsStore = create<IOrgDetailsStore>((set) => ({
  orgData: {
    response: {
      advisorFirmName: '',
      advisorFCAReferenceNumber: '',
      advisorRegistrationNumber: '',
      website: '',
      email: '',
      countryCode: '',
      mobile: '',
      firmAddress: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        addressLine5: '',
        city: '',
        county: '',
        country: '',
        postcode: '',
      },
      firmLogoBytes: '',
      firmLogoDocumentType: '',
    },
  },
  status: false,
  updateOrgData: (x: IGetOrganisationProfileResponse, status: boolean) => set(() => ({ orgData: x, status: status })),
}));

export const useUserAccessStore = create<IUserAccessStore>((set) => ({
  userAccess: null as IUserAccess | null,
  updateUserAccess: (x: IUserAccess | IUserAccessInput) => set(() => ({ userAccess: normalizeUserAccess(x) })),
  loading: true,
  updateLoading: (x: boolean) => set(() => ({ loading: x })),
}));

export const useDefaultEnumsStore = create<IUseDefaultEnumsStore>((set) => ({
  defaultEnums: {},
  status: false,
  updateDefaultEnums: (x: any, status: boolean) => set(() => ({ defaultEnums: x, status: status })),
}));

export default useUserDetailsStore;
